import React, { useState } from "react";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { UserCheck, Plus, RefreshCw, Search, X, FileText, ChevronRight, Phone, Mail, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

// Define form validation schema
const contractorFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }).optional().nullable(),
  phone: z.string().optional().nullable(),
  address: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    country: z.string().default("USA")
  }).optional().nullable(),
  taxId: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  status: z.enum(["active", "inactive"]).default("active")
});

type ContractorFormValues = z.infer<typeof contractorFormSchema>;

type ContractorRecord = ContractorFormValues & {
  id: number;
  businessId: number;
  createdAt: string;
  updatedAt: string;
};

export default function ContractorsPage() {
  const { toast } = useToast();
  const { currentBusiness: selectedBusiness } = useCurrentBusiness();
  const [isAddContractorOpen, setIsAddContractorOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [_, navigate] = useLocation();
  const queryClient = useQueryClient();
  
  // Fetch contractors
  const {
    data: contractors = [],
    isLoading: isLoadingContractors,
    isError,
    error,
    refetch
  } = useQuery<ContractorRecord[], Error>({
    queryKey: [`/api/businesses/${selectedBusiness?.id}/contractors`],
    queryFn: async () => {
      if (!selectedBusiness?.id) return [];
      const res = await apiRequest("GET", `/api/businesses/${selectedBusiness.id}/contractors`);
      return res.json() as Promise<ContractorRecord[]>;
    },
    enabled: !!selectedBusiness?.id
  });
  
  // Initialize the form
  const form = useForm<ContractorFormValues>({
    resolver: zodResolver(contractorFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "USA"
      },
      taxId: "",
      notes: "",
      status: "active"
    }
  });
  
  // Set up mutation for adding a contractor
  const addContractorMutation = useMutation({
    mutationFn: async (data: ContractorFormValues) => {
      if (!selectedBusiness) {
        throw new Error("No business selected");
      }
      
      const response = await apiRequest("POST", `/api/businesses/${selectedBusiness.id}/contractors`, {
        ...data,
        businessId: selectedBusiness.id
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to add contractor");
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Force refresh contractors data
      refetch();
      
      // Also invalidate the query cache
      queryClient.invalidateQueries({ 
        queryKey: [`/api/businesses/${selectedBusiness?.id}/contractors`] 
      });
      
      toast({
        title: "Contractor added",
        description: "The contractor has been added successfully",
      });
      
      setIsAddContractorOpen(false);
      form.reset();
    },
    onError: (error) => {
      console.error("Contractor addition error:", error);
      toast({
        title: "Failed to add contractor",
        description: error.message || "There was an error adding the contractor",
        variant: "destructive",
      });
    }
  });

  if (!selectedBusiness) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh] flex-col">
          <UserCheck className="h-16 w-16 text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Business Selected</h2>
          <p className="text-gray-600 mb-4">Please select or create a business to manage contractors.</p>
        </div>
      </Layout>
    );
  }

  // Handle form submission
  const onSubmit = (data: ContractorFormValues) => {
    addContractorMutation.mutate(data);
  };

  return (
    <Layout>
      
      {/* Add Contractor Dialog */}
      <Dialog open={isAddContractorOpen} onOpenChange={setIsAddContractorOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Add New Contractor</DialogTitle>
            <DialogDescription>
              Add a contractor to track payments and generate 1099 forms.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contractor Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter contractor name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Email address" {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="Phone number" {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="address.street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street</FormLabel>
                        <FormControl>
                          <Input placeholder="Street address" {...field} value={field.value || ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address.city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City</FormLabel>
                        <FormControl>
                          <Input placeholder="City" {...field} value={field.value || ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="address.state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State</FormLabel>
                        <FormControl>
                          <Input placeholder="State/Province" {...field} value={field.value || ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address.zipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Zip Code</FormLabel>
                        <FormControl>
                          <Input placeholder="Zip/Postal code" {...field} value={field.value || ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address.country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <Input placeholder="Country" {...field} value={field.value || "USA"} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              
              <FormField
                control={form.control}
                name="taxId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax ID (SSN/EIN)</FormLabel>
                    <FormControl>
                      <Input placeholder="Tax identification number" {...field} value={field.value || ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Input placeholder="Any notes about this contractor" {...field} value={field.value || ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter className="mt-6">
                <Button variant="outline" type="button" onClick={() => setIsAddContractorOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={addContractorMutation.isPending}
                >
                  {addContractorMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : "Add Contractor"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Contractors</h1>
            <p className="mt-1 text-sm text-neutral-500">
              Manage your independent contractors and 1099 workers
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <Button 
              variant="outline" 
              onClick={() => refetch()} 
              disabled={isLoadingContractors}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingContractors ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => setIsAddContractorOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Contractor
            </Button>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search contractors..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Contractors tabs */}
        <Tabs defaultValue="all" className="mt-6">
          <TabsList className="mb-6">
            <TabsTrigger value="all">All Contractors</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="inactive">Inactive</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle>All Contractors</CardTitle>
                <CardDescription>View and manage all your contractors</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingContractors ? (
                  <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : contractors.length === 0 ? (
                  <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                    <div className="text-center">
                      <UserCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">No contractors yet</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Add your first contractor to start tracking your 1099 workers
                      </p>
                      <div className="mt-6">
                        <Button onClick={() => setIsAddContractorOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Contractor
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Tax ID</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Documents</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contractors
                          .filter(contractor => 
                            searchQuery === "" || 
                            contractor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (contractor.email && contractor.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
                            (contractor.phone && contractor.phone.includes(searchQuery))
                          )
                          .map((contractor) => (
                            <TableRow 
                              key={contractor.id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => navigate(`/expenses/contractors/${contractor.id}`)}
                            >
                              <TableCell className="font-medium">{contractor.name}</TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  {contractor.email && (
                                    <div className="flex items-center text-sm">
                                      <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
                                      <span>{contractor.email}</span>
                                    </div>
                                  )}
                                  {contractor.phone && (
                                    <div className="flex items-center text-sm mt-1">
                                      <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                                      <span>{contractor.phone}</span>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{contractor.taxId || "-"}</TableCell>
                              <TableCell>
                                <Badge variant={contractor.status === "active" ? "default" : "secondary"}>
                                  {contractor.status === "active" ? "Active" : "Inactive"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/expenses/contractors/${contractor.id}?tab=agreements`);
                                  }}
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  Agreements
                                </Button>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/expenses/contractors/${contractor.id}`);
                                  }}
                                >
                                  Details
                                  <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="active">
            <Card>
              <CardHeader>
                <CardTitle>Active Contractors</CardTitle>
                <CardDescription>Contractors you currently work with</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingContractors ? (
                  <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : contractors.filter(c => c.status === "active").length === 0 ? (
                  <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                    <div className="text-center">
                      <p className="text-gray-500">No active contractors found</p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Tax ID</TableHead>
                          <TableHead>Documents</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contractors
                          .filter(contractor => contractor.status === "active")
                          .filter(contractor => 
                            searchQuery === "" || 
                            contractor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (contractor.email && contractor.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
                            (contractor.phone && contractor.phone.includes(searchQuery))
                          )
                          .map((contractor) => (
                            <TableRow 
                              key={contractor.id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => navigate(`/expenses/contractors/${contractor.id}`)}
                            >
                              <TableCell className="font-medium">{contractor.name}</TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  {contractor.email && (
                                    <div className="flex items-center text-sm">
                                      <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
                                      <span>{contractor.email}</span>
                                    </div>
                                  )}
                                  {contractor.phone && (
                                    <div className="flex items-center text-sm mt-1">
                                      <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                                      <span>{contractor.phone}</span>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{contractor.taxId || "-"}</TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/expenses/contractors/${contractor.id}?tab=agreements`);
                                  }}
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  Agreements
                                </Button>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/expenses/contractors/${contractor.id}`);
                                  }}
                                >
                                  Details
                                  <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="inactive">
            <Card>
              <CardHeader>
                <CardTitle>Inactive Contractors</CardTitle>
                <CardDescription>Contractors you no longer work with</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingContractors ? (
                  <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : contractors.filter(c => c.status === "inactive").length === 0 ? (
                  <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                    <div className="text-center">
                      <p className="text-gray-500">No inactive contractors found</p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Tax ID</TableHead>
                          <TableHead>Documents</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contractors
                          .filter(contractor => contractor.status === "inactive")
                          .filter(contractor => 
                            searchQuery === "" || 
                            contractor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (contractor.email && contractor.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
                            (contractor.phone && contractor.phone.includes(searchQuery))
                          )
                          .map((contractor) => (
                            <TableRow 
                              key={contractor.id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => navigate(`/expenses/contractors/${contractor.id}`)}
                            >
                              <TableCell className="font-medium">{contractor.name}</TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  {contractor.email && (
                                    <div className="flex items-center text-sm">
                                      <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
                                      <span>{contractor.email}</span>
                                    </div>
                                  )}
                                  {contractor.phone && (
                                    <div className="flex items-center text-sm mt-1">
                                      <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                                      <span>{contractor.phone}</span>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{contractor.taxId || "-"}</TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/expenses/contractors/${contractor.id}?tab=agreements`);
                                  }}
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  Agreements
                                </Button>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/expenses/contractors/${contractor.id}`);
                                  }}
                                >
                                  Details
                                  <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
